import { Tag, TagGetByLabelOptions } from 'contensis-management-api';
import pl from 'pluralize';

import TagGroupsMigrationService from './TagGroupsMigrationService';
import {
  MigrateRequest,
  ProjectMigrateTag,
  ProjectMigrateTagGroup,
  TagsResult,
} from '~/models/Operations';
import ContensisRepository from '~/repositories/ContensisRepository';
import { TagBuilder } from '~/transformations/TagBuilder';
import {
  logMigrateResultsTable,
  logTagsPreviewTable,
} from '~/transformations/logging';
import { dedupeInner } from '~/util/array';
import {
  completeProgressBar,
  logError,
  Logger,
  newProgressBar,
} from '~/util/logger';
import { isArray } from '~/util/typeguards';

export type GetTagsArgs = Partial<TagGetByLabelOptions> & {
  id?: string;
  ids?: string[];
};
class TagsMigrationService extends TagGroupsMigrationService {
  /** Keep a note of the tags we have migrated in this job */
  tagsMigration: {
    created: string[];
    updated: string[];
    deleted: string[];
  } = {
    created: [],
    updated: [],
    deleted: [],
  };

  constructor(payload: MigrateRequest, preview: boolean) {
    super(payload, preview);
  }

  GetTags = async (
    getBy: GetTagsArgs = {},
    { withDependents } = { withDependents: false }
  ) => {
    if (withDependents) {
      await this.GetTagsToMigrate(getBy);

      return [
        this.source.tags.error,
        [...this.source.tags.groups, ...this.source.tags.raw],
      ] as [ApiErrorResponse, undefined] | [null, Tag[]];
    }
    // Just gets tags from source repo
    return await this.source.tags.GetTags(getBy);
  };

  MigrateTags = async (getBy: GetTagsArgs = {}) => {
    const { isPreview } = this;

    // Loads content types and components into memory
    this.payload.includeDefaults = false;
    if (!this.isHydrated) await this.HydrateContensisRepositories();

    // Loads all tags from source and target repos into memory
    await this.GetTagsToMigrate(getBy);

    // Build and store the target tags
    // against each targetRepo.migrateTags
    await this.BuildTagGroupsToMigrate();
    await this.BuildTagsToMigrate();

    const previewResult = this.mapTagsMigrateResult();
    if (Logger.shouldLog('warning')) {
      const projectIds = Object.keys(this.targets);
      logTagsPreviewTable(previewResult, projectIds);
    }

    if (!isPreview && this.errorCount && !this.ignoreErrors) {
      Logger.error(
        `[tags] Not committing ${this.opName} due to ${this.errorCount} errors`
      );
    } else if (!isPreview) {
      if (this.ignoreErrors)
        Logger.warning(
          `[tags] Committing ${this.opName} with ${this.errorCount} known errors`
        );

      if (!isPreview) {
        await this.CommitTags();
      }
    }

    let result = previewResult;
    if (!isPreview) {
      result = this.mapTagsMigrateResult();
    }
    if (result.groupsResult) logMigrateResultsTable(result, 'taggroups');
    logMigrateResultsTable(result, 'tags');

    Logger.success(
      `Tags ${this.opName} ${isPreview ? `preview ready` : 'complete'}`,
      true
    );

    return result;
  };

  GetTagsToMigrate = async ({
    groupId,
    id,
    ids,
    label,
    language,
  }: GetTagsArgs = {}) => {
    const { source, targets } = this;

    const hasTagsInPayload = source.tags.raw?.length > 0;

    // Entries are not supplied in the payload
    if (!hasTagsInPayload) {
      if (source.repo) {
        // Get tags from source repo
        await source.tags.GetTags({
          groupId,
          id,
          ids,
          label,
          language,
        });
      } else {
        const [err, targetTags] = await Object.values(targets)[0].tags.GetTags({
          groupId,
          id,
          ids,
          label,
          language,
        });
        if (isArray(targetTags)) source.tags.raw = targetTags;
      }
    }

    if (source.tags.raw.length > 0) {
      const groupIds = new Set(source.tags.raw.map((t) => t.groupId));
      void this.queue.add(() =>
        this.GetTagGroupsToMigrate({ ids: [...groupIds] })
      );

      for (const [, target] of Object.entries(targets)) {
        // Fetch all tags from target repo
        void this.queue.add(() => target.tags.GetTags());
      }
    }

    // Wait until we've got all repo tags (and groups)
    await this.queue.onIdle();
  };

  BuildTagsToMigrate = async () => {
    // Build tags for each target repo
    for (const [, target] of Object.entries(this.targets)) {
      await target.tags.BuildTags({
        onError: (b) => this.AddError(b.originalId || '', b.error),
      });
    }
  };

  CommitTags = async () => {
    for (const [, target] of Object.entries(this.targets)) {
      await this.QueueTagGroups(target);
      await this.QueueTags(target);
    }
  };

  QueueTags = async (target: ContensisRepository) => {
    const migrateTags = target.tags.migrateTags.filter((mt) =>
      ['create', 'update'].includes(mt.status)
    );
    const totalTags = migrateTags.length;

    if (totalTags)
      Logger.info(
        `[tags] Committing ${totalTags} ${pl('tag', totalTags)} in ${Logger.highlightText(
          target.alias
        )} ${Logger.standardText(target.project.id)}`,
        true
      );

    const pr = newProgressBar(
      ` Loading [${target.project.id}] :bar :percent ${Logger.infoText(
        ':current/:total :elapseds/:etas :ratep/s'
      )}`,
      {
        output: this.payload.outputProgress,
        complete: Logger.successText('='),
        head: Logger.successText('>'),
        incomplete: ' ',
        width: 30,
        total: totalTags,
      }
    );

    let currentIndex = 0;

    for (const migrateTag of migrateTags) {
      // use queue so we can load tags in parallel
      void this.queue.add(async () => {
        Logger.info(
          `[tags] ${++currentIndex}/${totalTags} [${
            migrateTag.status
          }] '${migrateTag.source.value}' | ${migrateTag.source.id}`
        );
        await this.CommitTag(migrateTag);
        pr?.tick();
      });
    }

    await this.queue.onIdle();

    completeProgressBar();
  };

  CommitTag = async (
    migrateTag: TagBuilder
  ): Promise<[AppError | null, Tag | undefined]> => {
    if (!migrateTag.error) {
      const sourceTag = migrateTag.source;

      const upsertTag = migrateTag.toJSON();

      // Upsert tag
      const [error, newTag] = await migrateTag.contensis.repo.UpdateOrCreateTag(
        upsertTag,
        migrateTag.existing
      );

      if (!error && newTag) {
        if (migrateTag.status === 'create') {
          this.tagsMigration.created.push(sourceTag.id);
        } else if (migrateTag.status === 'update') {
          this.tagsMigration.updated.push(sourceTag.id);
        }
      }

      if (error) {
        const mappedError = this.AddError(sourceTag.id, error);
        migrateTag.error = mappedError;
        logError(
          error.data || error,
          `${mappedError.message} | ${upsertTag.id}`
        );
      }
      return [error, newTag];
    }
    return [migrateTag.error, undefined];
  };

  // TODO: should be protected
  public mapTagsMigrateResult = () => {
    const { source, targets } = this;

    const sourceProjectId =
      source.projectId && source.projectId !== 'null'
        ? source.projectId
        : Object.values(targets)[0].projectId;

    const sourceTags =
      this.opName === 'delete'
        ? Object.values(targets)
            .map((target) => target.tags.migrateTags.map((mt) => mt.source))
            .flat()
        : source.tags.raw;

    const targetGroupdIds = [
      ...new Set(
        Object.values(targets)
          .map((target) =>
            target.tags.migrateTags.map((mg) => mg.source.groupId)
          )
          .flat()
      ),
    ];
    const sourceGroupIds = (
      this.opName === 'delete'
        ? targetGroupdIds
        : [
            ...new Set([
              ...targetGroupdIds,
              ...source.tags.groups.map((g) => g.id),
            ]),
          ]
    ).sort();

    const result = {
      tags: sourceProjectId
        ? { [sourceProjectId]: { totalCount: 0 } }
        : undefined,
      errors: Object.keys(this.errors).length
        ? dedupeInner(Object.values(this.errors).flat())
        : undefined,
      groupsResult: !this.isPreview
        ? {
            created: this.groupsMigration.created.length,
            updated: this.groupsMigration.updated.length,
            deleted: this.groupsMigration.deleted.length,
            errors: Object.values(this.errors).flat().length || undefined,
          }
        : undefined,
      tagsResult: !this.isPreview
        ? {
            created: this.tagsMigration.created.length,
            updated: this.tagsMigration.updated.length,
            deleted: this.tagsMigration.deleted.length,
            errors: Object.values(this.errors).flat().length || undefined,
          }
        : undefined,
      existing: Object.fromEntries(
        Object.keys(targets).map((projectId) => [projectId, { totalCount: 0 }])
      ),
      groupsToMigrate:
        this.opName !== 'delete'
          ? {
              ...Object.fromEntries(
                Object.keys(targets).map((projectId) => [projectId, {}])
              ),
              groupIds: {},
            }
          : undefined,
      tagsToMigrate: {
        ...Object.fromEntries(
          Object.keys(targets).map((projectId) => [projectId, {}])
        ),
        tagIds: Object.fromEntries(
          sourceGroupIds.map((sourceGroupId) => [
            sourceGroupId,
            Object.fromEntries(
              sourceTags
                .filter((t) => t.groupId === sourceGroupId)
                .map((sourceTag) => [
                  sourceTag.id,
                  {
                    label: sourceTag.label,
                    value: sourceTag.value,
                    ...Object.fromEntries(
                      Object.entries(targets)
                        .map(([projectId, contensis]) => {
                          const mt = contensis.tags.migrateTags.find(
                            (mt) => mt.originalId === sourceTag.id
                          );
                          if (!mt) return [];
                          return [
                            projectId,
                            {
                              id: mt.existing?.id || mt.id,
                              diff: mt.diff,
                              error: mt.error?.message,
                              status: mt.status,
                            },
                          ];
                        })
                        .filter((v) => v.length) as [
                        string,
                        ProjectMigrateTag,
                      ][]
                    ),
                  },
                ])
            ),
          ])
        ),
      },
    } as TagsResult;
    if (this.opName !== 'delete')
      result.groupsToMigrate.groupIds = Object.fromEntries(
        sourceGroupIds.map((sourceGroupId) => {
          const tagGroup =
            source.context.tagGroups.find((g) => g.id === sourceGroupId) ||
            Object.values(targets)[0]
              .tags.migrateGroups.find((mg) => mg.id === sourceGroupId)
              ?.toJSON() ||
            source.tags.groups.find((g) => g.id === sourceGroupId);

          return [
            sourceGroupId,
            {
              name: tagGroup?.name,
              description: tagGroup?.description,
              ...Object.fromEntries(
                Object.entries(targets)
                  .map(([projectId, contensis]) => {
                    // Find a group to migrate from migrateGroups
                    const mg = contensis.tags.migrateGroups.find(
                      (mg) => mg.id === sourceGroupId
                    );
                    if (!mg) {
                      // The group is not being migrated
                      // Find a tag we've built to migrate in the target
                      // containing this group and return an assumed status
                      return contensis.tags.migrateTags.find(
                        (mt) => mt.source.groupId === sourceGroupId
                      )
                        ? [
                            projectId,
                            {
                              status: contensis.context.tagGroups.find(
                                (g) => g.id === sourceGroupId
                              )
                                ? 'no change'
                                : 'unknown',
                            },
                          ]
                        : [];
                    }
                    return [
                      projectId,
                      {
                        status: mg.status,
                        diff: mg.diff,
                        error: mg.error?.message,
                      },
                    ];
                  })
                  .filter((v) => v.length) as [string, ProjectMigrateTagGroup][]
              ),
            },
          ];
        })
      );
    for (const [projectId, target] of Object.entries(targets)) {
      result.existing[projectId] = {
        totalCount: target.tags.raw.length,
        tags: target.tags.raw,
      };

      const statuses = [
        ...new Set(target.tags.migrateTags.map((mg) => mg.status)),
      ];
      if (this.opName !== 'delete') {
        const groupStatuses = [
          ...new Set(target.tags.migrateGroups.map((mg) => mg.status)),
        ];
        result.groupsToMigrate[projectId] = Object.fromEntries([
          ...groupStatuses.map((status) => [
            status,
            target.tags.migrateGroups.filter((mg) => mg.status === status)
              .length,
          ]),
          [
            'totalCount',
            target.tags.migrateGroups?.filter((mg) =>
              ['create', 'update', 'delete'].includes(mg.status)
            )?.length || 0,
          ],
        ]);
      }
      result.tagsToMigrate[projectId] = Object.fromEntries([
        ...statuses.map((status) => [
          status,
          target.tags.migrateTags.filter((mt) => mt.status === status).length,
        ]),
        [
          'totalCount',
          target.tags.migrateTags?.filter((mt) =>
            ['create', 'update', 'delete'].includes(mt.status)
          )?.length || 0,
        ],
      ]);
    }
    if (result.tags) {
      result.tags[sourceProjectId] = {
        ...Object.fromEntries(
          // add tag `groupId: tagCount` from migrateTags
          sourceGroupIds.map((sourceGroupId) => [
            sourceGroupId,
            Object.values(targets).reduce((sum, target) => {
              return (
                sum +
                target.tags.migrateTags.reduce((innerSum, tags) => {
                  return (
                    innerSum + (tags.source.groupId === sourceGroupId ? 1 : 0)
                  );
                }, 0)
              );
            }, 0),
          ])
        ),
        totalCount: source.tags.raw.length,
        totalGroups: sourceGroupIds.length,
      };
    }
    return result;
  };

  DeleteTags = async (getBy: GetTagsArgs, targets = this.targets) => {
    const { isPreview } = this;
    this.opName = 'delete';

    // Loads all tags from source and target repos into memory
    await this.GetTagsToDelete(getBy, targets);

    if (!isPreview) {
      for (const [projectId, target] of Object.entries(targets)) {
        Logger.info(
          `Deleting tags in project ${Logger.highlightText(projectId)}`,
          true
        );

        for (const [i, migrateTag] of Object.entries(
          target.tags.migrateTags?.filter(
            (mt) => mt.status === 'delete' && !!mt.existing
          ) || []
        )) {
          void this.queue.add(async () => {
            const tag = migrateTag.existing as Tag;

            Logger.warning(
              `[delete] ${Number(i) + 1}/${
                target.tags.raw.length
              } ${projectId} [${tag.groupId}] ${tag.value} ${tag.id}`
            );
            // TODO: could be bulk deleting here sorting ids by group
            const [error] = await target.repo.DeleteTag(tag.id);
            if (error) {
              Logger.error(
                `Error attempting to delete tag ${tag.id}. Error was ${JSON.stringify(error)}`
              );
              const mappedError = this.AddError(tag.id, error);
              migrateTag.error = mappedError;
              migrateTag.status = 'error';
            } else this.tagsMigration.deleted.push(tag.id);
          });
        }
      }
    }

    await this.queue.onIdle();

    Logger.info(
      `Delete tags ${isPreview ? 'preview ready' : 'completed'}`,
      true
    );
    return this.mapTagsMigrateResult();
  };

  GetTagsToDelete = async (getBy: GetTagsArgs, targets = this.targets) => {
    const { source } = this;

    // If a source is supplied we will read entries from here and pass the
    // transformed ids to the GetEntries call when fetching entries to delete
    // from the target repos
    if (source.repo?.ProjectId() && Object.keys(targets).length) {
      await source.tags.GetTags(getBy);
    }

    const promises = [] as Promise<any>[];

    for (const [, target] of Object.entries(targets)) {
      // Fetch tags from target repo based on transformed tag ids
      // If there isn't any source tags to transform GetTags will
      // fetch based on the provided args
      const tagIds = source.tags.raw.length
        ? Object.fromEntries(
            source.tags.raw.map((t) => [
              target.models.isSourceRepo
                ? target.transformGuid(t.id || '')
                : t.id,
              t.id,
            ])
          )
        : undefined;

      promises.push(
        (async () => {
          const [err, deleteTags] = await target.tags.GetTags({
            ...getBy,
            ids: tagIds ? Object.keys(tagIds) : getBy.ids,
          });
          if (err) this.AddError('*', err);
          else {
            deleteTags.map((t) => {
              const tagBuilder = new TagBuilder(target, source, t);
              tagBuilder.existing = t;
              if (t.usageCount) {
                tagBuilder.status = 'error';
                tagBuilder.error = new Error(
                  `Cannot delete tag that is currently in use`
                );
                this.AddError(tagBuilder.originalId, tagBuilder.error);
              } else tagBuilder.status = 'delete';
              target.tags.migrateTags.push(tagBuilder);
            });
          }
        })()
      );
    }

    // Get all repo tags at the same time
    await Promise.all(promises);
  };
}

export default TagsMigrationService;
